import type { Category, Flag, ScoreResult, VisitorIdentity } from '@fraudeai/schema';

const VERDICT_TEXT: Record<ScoreResult['verdict'], { title: string; hint: string; color: string }> = {
  clean: {
    title: 'Окружение выглядит настоящим',
    hint: 'Проверки не нашли признаков подмены, автоматизации или рассогласования слоёв.',
    color: 'var(--clean)',
  },
  suspicious: {
    title: 'Есть подозрительные признаки',
    hint: 'Отдельные сигналы не сходятся между собой. По одному это объяснимо, но антифрод-система обратит внимание.',
    color: 'var(--suspicious)',
  },
  high_risk: {
    title: 'Высокий риск',
    hint: 'Найдены явные расхождения между слоями. Такое окружение большинство антифрод-систем помечает как рискованное.',
    color: 'var(--high-risk)',
  },
  network_risk: {
    title: 'Браузер настоящий, вопросы к сети',
    hint:
      'Признаков подмены самого браузера не найдено. Оценку снижает соединение: ' +
      'туннель, адрес дата-центра или утечки мимо него. Антифрод-системы это ' +
      'учитывают, но подделкой окружения не считают.',
    color: 'var(--suspicious)',
  },
  genuine_browser_with_vpn: {
    title: 'Настоящий браузер через VPN',
    hint:
      'Слои браузера согласованы, признаков антидетекта или автоматизации не найдено. ' +
      'Риск даёт сеть: адрес VPN, прокси, Tor или дата-центра. Это важно для антифрода, ' +
      'но не доказывает подмену самого браузера.',
    color: 'var(--suspicious)',
  },
  fraudulent: {
    title: 'Окружение подделано',
    hint: 'Подмена видна сразу по нескольким независимым признакам. Скрыться с такой конфигурацией не получится.',
    color: 'var(--fraudulent)',
  },
};

/**
 * Отдельная формулировка для случая, когда оценку ограничило не найденное,
 * а непроверенное.
 *
 * Без неё пользователь с исправным браузером и недоступным сетевым слоем
 * читал «есть подозрительные признаки» — хотя ничего подозрительного не
 * нашли, просто не смотрели. Число в обоих случаях получается похожее,
 * а означает противоположное, и путать их нельзя.
 */
const LIMITED_TEXT: Record<NonNullable<ScoreResult['limitedBy']>, { title: string; hint: string }> = {
  'network-layer': {
    title: 'Проверка неполная',
    hint:
      'Отпечатки соединения — TLS и HTTP/2 — снять не удалось. Именно они ловят ' +
      'антидетект-браузеры: те бывают безупречно согласованы внутри страницы и ' +
      'выдают себя только сетевым стеком. Поэтому оценка ограничена сверху: это ' +
      'не «чисто», а «не проверяли».',
  },
  coverage: {
    title: 'Проверка неполная',
    hint:
      'Часть измерений не отработала, поэтому оценка ограничена сверху. ' +
      'Непроверенное не выдаётся за чистое — подробности в таблице проверок ниже.',
  },
};

const FLAG_LABELS: Record<Flag, string> = {
  PROXY_VPN: 'прокси или VPN',
  HOSTING: 'адрес дата-центра',
  TOR: 'сеть Tor',
  ANTIDETECT: 'антидетект-браузер',
  AUTOMATION: 'автоматизация',
  SPOOFING: 'подмена параметров',
  INCONSISTENT: 'рассогласование слоёв',
  BOT: 'бот',
  VIRTUAL_MACHINE: 'виртуальная машина',
  PRIVACY_TOOLING: 'инструменты приватности',
};

/** Флаги, которые сами по себе не обвиняют, а лишь описывают конфигурацию. */
const SOFT_FLAGS: Flag[] = ['PRIVACY_TOOLING', 'VIRTUAL_MACHINE'];

const CATEGORY_LABELS: Record<Category, string> = {
  spoofing: 'Подмена данных',
  automation: 'Автоматизация',
  inconsistency: 'Рассогласование',
  hardware: 'Железо',
  network: 'Сеть',
  privacy: 'Приватность',
  identity: 'Идентификация',
};

function colorForScore(score: number): string {
  if (score >= 85) return 'var(--clean)';
  if (score >= 65) return 'var(--suspicious)';
  if (score >= 35) return 'var(--high-risk)';
  return 'var(--fraudulent)';
}

function Gauge({ score }: { score: number }) {
  const radius = 72;
  const circumference = 2 * Math.PI * radius;
  const filled = (score / 100) * circumference;

  return (
    <div className="gauge">
      <svg width="168" height="168" viewBox="0 0 168 168" aria-hidden="true">
        <circle cx="84" cy="84" r={radius} fill="none" stroke="var(--bg-sunken)" strokeWidth="11" />
        <circle
          cx="84"
          cy="84"
          r={radius}
          fill="none"
          stroke={colorForScore(score)}
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference - filled}`}
        />
      </svg>
      <div className="gauge-value">
        <strong style={{ color: colorForScore(score) }}>{score}</strong>
        <span>trust score</span>
      </div>
    </div>
  );
}

export function Verdict({ score, identity }: { score: ScoreResult; identity: VisitorIdentity }) {
  const base = VERDICT_TEXT[score.verdict];
  const limited = score.limitedBy ? LIMITED_TEXT[score.limitedBy] : null;
  const networkCoverage = score.networkCoverage ?? 0;
  const confidence = score.confidence ?? 1;
  // Когда оценку прижал потолок, а не находки, заголовок говорит именно об этом.
  const verdict = limited
    ? { ...limited, color: 'var(--suspicious)' }
    : base;

  // Обвинение без прямой улики. По устройству оценки такое обвинение держится
  // на согласии независимых косвенных групп (уверенность около 0.72), тогда
  // как прямая улика даёт 0.85 и выше. Порог 0.8 разделяет эти два случая.
  // Публичный чекер обязан сказать об этом прямо: косвенное обвинение — повод
  // для дополнительной проверки, а не для приговора. Случай «чисто, но
  // неуверенно» уже перехватывает limited выше.
  const accusation =
    score.verdict === 'suspicious' ||
    score.verdict === 'high_risk' ||
    score.verdict === 'fraudulent';
  const indirectAccusation = accusation && confidence < 0.8 && !limited;

  return (
    <>
      <div className="verdict">
        <Gauge score={score.trustScore} />
        <div>
          <h2 style={{ color: verdict.color }}>{verdict.title}</h2>
          <p>{verdict.hint}</p>
          {indirectAccusation && (
            <p className="hint" style={{ marginTop: -4 }}>
              Вывод опирается на косвенные признаки, а не на прямую улику. Антифрод-система,
              скорее всего, назначила бы такому окружению дополнительную проверку, а не
              блокировала бы сразу.
            </p>
          )}
          {/*
            Две оценки показываются отдельно, потому что отвечают на разные
            вопросы: «кто пришёл» и «откуда пришёл». Одно общее число их
            смешивало, и честный браузер за протекающим VPN выглядел хуже
            антидетект-сборки через аккуратный прокси.
          */}
          <div className="split-scores">
            <span>
              Подлинность браузера <b>{score.authenticity}</b>
            </span>
            <span>
              Доверие к сети <b>{score.networkTrust}</b>
            </span>
            <span>
              Покрытие сети <b>{Math.round(networkCoverage * 100)}%</b>
            </span>
            {/*
              Уверенность вывода отдельно от trust score: одно отвечает
              «насколько подозрительно», другое — «насколько мы вообще уверены».
              Смешивать их нельзя, иначе слабое обвинение читается как твёрдое.
            */}
            <span>
              Уверенность вывода <b>{Math.round(confidence * 100)}%</b>
            </span>
          </div>

          {score.flags.length > 0 && (
            <div className="flags">
              {score.flags.map((flag) => (
                <span
                  key={flag}
                  className="flag"
                  data-tone={SOFT_FLAGS.includes(flag) ? 'soft' : undefined}
                >
                  {FLAG_LABELS[flag]}
                </span>
              ))}
            </div>
          )}

          <p style={{ marginTop: 14, marginBottom: 0, fontSize: 13 }}>
            Идентификатор устройства <code style={{ color: 'var(--text)' }}>{identity.visitorId}</code>
            {' · '}
            надёжность {Math.round(identity.confidence * 100)}%
            {' · '}
            проверок выполнено {Math.round(score.coverage * 100)}%
            {' · '}
            сеть {Math.round(networkCoverage * 100)}%
          </p>
        </div>
      </div>

      {score.byCategory.length > 0 && (
        <section>
          <h3>По направлениям</h3>
          <p className="hint">
            Каждое направление оценивается отдельно: низкий балл показывает, где именно
            окружение выглядит неубедительно.
          </p>
          <div className="categories">
            {score.byCategory.map((category) => (
              <div className="category" key={category.category}>
                <div className="category-name">
                  <span>{CATEGORY_LABELS[category.category]}</span>
                  <b>{category.score}</b>
                </div>
                <div className="meter">
                  <div
                    style={{
                      width: `${category.score}%`,
                      background: colorForScore(category.score),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
